import React, { memo, useCallback } from 'react';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { EditorStarterItem } from '../../items/item-type';
import { useFps, useItem } from '../../utils/use-context';
import { useTimingInput } from '@waka/compat/use-timing-input';
import { useSelectionValues } from '../utils/use-selection-values';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { NumberControlUpdateHandler } from './number-controls';
import {
  canEditTimingInInspector,
  framesToRoundedSeconds,
} from './timing-controls-utils';

const FRAME_STEP = 1;

const TimingApproxSeconds: React.FC<{
  frames: number;
  fps: number;
}> = ({ frames, fps }) => {
  const t = useTranslations();

  return (
    <div className="mt-5xs text-caption-small text-sys-cont-neutral-light">
      {t('Editor.inspector.timing.approx-seconds', {
        seconds: framesToRoundedSeconds(frames, fps),
      })}
    </div>
  );
};

const TimingControlsUnmemoized: React.FC<{
  itemId: string;
}> = ({ itemId }) => {
  const t = useTranslations();
  const { fps } = useFps();
  const item = useItem(itemId);
  const targetIds = useTargetItemIds(itemId);
  const { begin, update, cancel, finishField, revision } = useTimingInput(
    itemId,
    targetIds,
  );

  const { isMixed: isStartMixed, value: selectedStartFrame } =
    useSelectionValues<EditorStarterItem, number>(itemId, (selectedItem) => {
      return selectedItem.from;
    });

  const { isMixed: isDurationMixed, value: selectedDurationInFrames } =
    useSelectionValues<EditorStarterItem, number>(itemId, (selectedItem) => {
      return selectedItem.durationInFrames;
    });

  const startFrame = selectedStartFrame ?? item?.from ?? 0;
  const durationInFrames =
    selectedDurationInFrames ?? item?.durationInFrames ?? 1;

  const setStartFrame = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      update('start', num, commitToUndoStack);
    },
    [update],
  );

  const setDurationFrames = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      update('duration', num, commitToUndoStack);
    },
    [update],
  );

  if (!item || !canEditTimingInInspector(item)) {
    return null;
  }

  return (
    <div
      className="flex gap-4xs"
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          cancel();
          if (event.target instanceof HTMLInputElement) event.target.blur();
        }
      }}
    >
      <div
        className="min-w-0 flex-1"
        onFocusCapture={() => begin('start', true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            finishField('start');
        }}
      >
        <ValueInputField
          key={revision.start}
          isShowLabel={true}
          label={t('Editor.inspector.timing.start')}
          value={startFrame}
          setValue={setStartFrame}
          min={0}
          max={null}
          step={FRAME_STEP}
          naturalOnly
          isUnit="f"
          accessibilityLabel={t('Editor.inspector.timing.start')}
          hasMixedValue={isStartMixed}
          className="w-full"
        />
        {!isStartMixed && <TimingApproxSeconds frames={startFrame} fps={fps} />}
      </div>
      <div
        className="min-w-0 flex-1"
        onFocusCapture={() => begin('duration', true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            finishField('duration');
        }}
      >
        <ValueInputField
          key={revision.duration}
          isShowLabel={true}
          label={t('Editor.inspector.timing.duration')}
          value={durationInFrames}
          setValue={setDurationFrames}
          min={1}
          max={null}
          step={FRAME_STEP}
          naturalOnly
          isUnit="f"
          accessibilityLabel={t('Editor.inspector.timing.duration')}
          hasMixedValue={isDurationMixed}
          className="w-full"
        />
        {!isDurationMixed && (
          <TimingApproxSeconds frames={durationInFrames} fps={fps} />
        )}
      </div>
    </div>
  );
};

export const TimingControls = memo(TimingControlsUnmemoized);
